import { useState, useEffect, useRef, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from 'react-toastify'
import { FaEdit, FaTrash } from "react-icons/fa";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const CollectorAccounts = () => {
    const [collectorAccounts, setCollectorAccounts] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [show, setShow] = useState(false);
    const [publicID, setPublicID] = useState()
    const gridRef = useRef();
    const navigate = useNavigate()

    useEffect(() => {
        fetchData()
    }, [])

    const fetchData = async () => {
        setIsLoading(true)
        try {
            const response = await Server.get('/collector-accounts/')
            setCollectorAccounts(response?.data)
        } catch (error) {
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail);
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load collector accounts')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: (params) => {
                if (isNaN(params.node.id)) return "";
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 120,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                return (
                    <div className="d-flex gap-1">
                        <Link className="btn btn-outline-info" to="/update-collector-account" state={{ account: e.data }} title="Edit">
                            <FaEdit/>
                        </Link>
                        <button className="btn btn-outline-danger" onClick={() => handleShow(e.data.public_id)} title="Delete">
                            <FaTrash/>
                        </button>
                    </div>
                );
            },
        },
        {
            headerName: "Name",
            field: "account_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Number",
            field: "account_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Collector",
            field: "collector_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Contact",
            field: "collector_contact",
            sortable: true,
            filter: true,
        },
    ], []);

    const handleClose = () => {
        if (isDeleting) return;
        setShow(false)
    }
    const handleShow = (public_id) => {
        setShow(true)
        setPublicID(public_id)
    };

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.delete(`/collector-accounts/${public_id}`)
            toast.success(response.data.detail)
            setCollectorAccounts(prev => prev.filter((account) => account.public_id !== public_id))
            setShow(false)
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail);
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to delete collector account')
            }
        } finally {
            setIsDeleting(false)
        }
    }

    const defaultColDef = useMemo(() => ({
        filterParams: { buttons: [] }
    }), []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <div className="row">
            <div className="col-md-12">
                <Link to='/add-collector-account' className="btn btn-outline-info mb-2" style={{float: 'right'}}>Add Account</Link>
            </div>
        </div>
        <div className="card">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">Collector Accounts</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                            <AgGridReact
                                suppressMenuHide={true}
                                defaultColDef={defaultColDef}
                                onFirstDataRendered={onFirstDataRendered}
                                onGridSizeChanged={onGridSizeChanged}
                                rowData={collectorAccounts}
                                domLayout={"normal"}
                                rowSelection={"multiple"}
                                animateRows={true}
                                pagination={false}
                                resizable={true}
                                enableCellTextSelection={true}
                                suppressCellFocus={true}
                                ref={gridRef}
                                columnDefs={columnDefs}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
            <Modal.Header closeButton={!isDeleting}>
                <Modal.Title>Delete Collector Account</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to delete this collector account? This action cannot be undone.</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isDeleting}>
                    No
                </Button>
                <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
                    {isDeleting ? 'Deleting…' : 'Yes, delete'}
                </Button>
            </Modal.Footer>
        </Modal>
    </>
  )
}

export default CollectorAccounts
